import { type Ref, ref, inject, type InjectionKey, provide } from 'vue'
import { useToggle } from './misc'
import { throwError } from '/@/core/errors'

export function triggerBlur(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return
  target.blur()
}

type ScrollOptions =
  | ({
      mode: 'into view'
    } & ScrollIntoViewOptions)
  | ({
      mode: 'just self' | 'whole page'
    } & ScrollToOptions)
type ScrollFunction = (options: ScrollOptions) => void
interface UseScrollControlReturn {
  ref: Ref<HTMLElement | null>
  scroll: ScrollFunction
  highlight: ReturnType<typeof useToggle>
}
function createScrollControlApi() {
  const _ref = ref<HTMLElement | HTMLElement[] | null>(null)
  const highlight = useToggle()

  function scroll({ mode, ...options }: ScrollOptions) {
    if (mode === 'into view') {
      if (Array.isArray(_ref.value)) {
        _ref.value?.[0].scrollIntoView(options)
      } else {
        _ref.value?.scrollIntoView(options)
      }
      highlight.on()
      setTimeout(() => {
        highlight.off()
      }, 4000)
      return
    }

    if (Array.isArray(_ref.value)) {
      _ref.value?.[0].scrollTo(options)
    } else {
      _ref.value?.scrollTo(options)
    }
    if (mode === 'whole page') {
      window.scrollTo(options)
    }
  }

  return { ref: _ref, scroll, highlight }
}
export function useScrollControl(): UseScrollControlReturn
export function useScrollControl<T extends string = string>(
  ids: T[],
): Record<T, UseScrollControlReturn>
export function useScrollControl(ids?: string[]) {
  if (Array.isArray(ids)) {
    return Object.fromEntries(ids.map(id => [id, createScrollControlApi()]))
  }

  return createScrollControlApi()
}

export const PageScrollControlKey: InjectionKey<UseScrollControlReturn> =
  Symbol('page-scroll-control')

export function installPageScrollControl() {
  const scrollControl = useScrollControl()
  provide(PageScrollControlKey, scrollControl)
  return scrollControl
}

export function usePageScrollControl() {
  const scrollControl = inject(PageScrollControlKey)
  if (!scrollControl) {
    throwError('Module page-scroll-control was not installed correctly.')
    return useScrollControl()
  }
  return scrollControl
}

export function checkScrollbarConfiguration() {
  // check if scrollbars are always visible
  const c = document.createElement('div')
  const s = document.createElement('div')
  const p = document.createElement('div')
  c.append(s)
  c.append(p)
  c.style.width = '320px'
  c.style.height = '200px'
  c.style.overflow = 'auto'
  s.style.position = 'sticky'
  s.style.top = '0px'
  s.style.width = '100%'
  s.style.height = '40px'
  p.style.width = '100%'
  p.style.height = '800px'
  document.body.append(c)
  const currentStickyWidth = s.getBoundingClientRect().width
  c.remove()
  p.remove()
  s.remove()
  if (currentStickyWidth !== 320) {
    return 'always-visible'
  }
  return 'overlay'
}
